/* DEPENDENCIES */
import './styles/App.scss';
import React, { useEffect, useRef, useState } from 'react';
import { 
  BrowserRouter as Router,
  Route,
  Redirect,
} from 'react-router-dom';
import { Provider } from 'react-redux';
/* COMPONENTS */
import AppContainer from './components/AppContainer';
import Loader from './components/Loader';
import NotificationsPrompt from './components/NotificationsPrompt';
import PushNotificationsRouter, {
  handlePushNotification,
} from './components/PushNotifications';
/* SCREENS */
import Login from './screens/Login';
import LoginOTP from './screens/LoginOTP';
import VerifyOTP from './screens/VerifyOTP';
import SignUp from './screens/SignUp';
import ResetPassword from './screens/ResetPassword';
import VerifyEmail from './screens/VerifyEmail';
import VerifyNationalIdentity from './screens/VerifyNationalIdentity';
import Profile from './screens/Profile';
import Promos from './screens/Promos';
import PromoDetail from './screens/PromoDetail';
import MyAccount from './screens/MyAccount';
//import AddRelative from './screens/AddRelative';
import Relatives from './screens/Relatives';
/* SERVICE */
import {getAssets} from './services/assets';
import { decrementUnread, refreshUnread } from './services/unread';
/* ERROR SCREENS */
import NotFound from './screens/404';
import Terms from './screens/Terms';
/* STORE */
import Store from './reducers';
import Notifications from './screens/Notifications';
/* NOTIFICATIONS */
import NotificationsV2 from './notificationsV2';

const App = () => {
  const [auth, setAuth] = useState(Store.getState().auth.isAuthenticated);
  const [redirect] = useState(window.location.pathname + window.location.search);
  const wasAuthenticated = useRef(auth);
  const unprotected = ['/ingresa', '/login-otp', '/verificar-codigo', '/registrate', '/recuperar-contrasena', "/terminos-y-condiciones"];

  useEffect(() => {
    console.log('VERSION', process.env.REACT_APP_VERSION);

    // GET ASSETS
    getAssets();

    const login = Store.subscribe(() => {
      setAuth(Store.getState().auth.isAuthenticated);
    });

    return function cleanup() {
      login();
    }
  }, []);

  useEffect(() => {
    // Registers this browser with the engine according to the brand's provider mode.
    NotificationsV2.start().then(() =>
      // Worker listeners, sessions and events only when the brand is on fcm_delivery.
      NotificationsV2.startDelivery({
        handlePushNotification,
        // A push while a tab is visible is not shown (as in the app); the pill has to be asked
        // for. False: nothing was displayed.
        onReceived: () => {
          refreshUnread();
          return false;
        },
        // A click reads that one notification.
        onRead: () => decrementUnread(),
      }).then(() => refreshUnread()),
    );
  }, []);

  useEffect(() => {
    // A login binds this browser's endpoint to the user; a session restored on page load was
    // already synced by start().
    if (auth && !wasAuthenticated.current) {
      NotificationsV2.onLogin().then(() => refreshUnread());
    }
    wasAuthenticated.current = auth;
  }, [auth]);

  useEffect(() => {
    // Pushes only ever raise the count. It comes down when the server says so — a read here, on
    // another device, or a notification that expired — so coming back to the tab asks again.
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Store.getState().auth.isAuthenticated) {
        refreshUnread();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  return (
    <Provider store={Store}>
        <Loader />
        <Router>
          <PushNotificationsRouter />
          <>
          <Route path='/ingresa' component={Login} />
          <Route path='/login-otp' component={LoginOTP} />
          <Route path='/verificar-codigo' component={VerifyOTP} />
          <Route path='/registrate' component={SignUp} />
          <Route path="/recuperar-contrasena" component={ResetPassword} />
          <Route path="/terminos-y-condiciones" component={Terms} />
          </>
          {
            auth ? (
              <>
                <AppContainer>
                  <Route path='/' component={Promos} exact />
                  <Route path='/familiares' component={Relatives} exact />
                  <Route path='/mi-cuenta/:tab?' component={MyAccount} exact />
                  <Route path='/notificaciones' component={Notifications} exact />
                  <Route path='/perfil' component={Profile} exact />
                  <Route path='/promo/:id' component={PromoDetail} exact />
                  <Route path='/404' component={NotFound} />
                </AppContainer>
                <Route path="/validar-email" component={VerifyEmail} />
                <Route path="/validar-id" component={VerifyNationalIdentity} />
                <NotificationsPrompt />
              </>
            ) : (
              unprotected.indexOf(window.location.pathname) === -1 && <Redirect to={`/ingresa${(redirect && redirect.indexOf('login') === -1 && redirect !== '/') ? `?path=${redirect}` : ''}`} />
            )
          }
      </Router>
    </Provider>
  );
};

export default App;
